const DOTS_PER_MM = 8;
const BITS_PER_BYTE = 8;

const STATUS_BITS = [
    'head_opened',
    'paper_jam',
    'out_of_paper',
    'out_of_ribbon',
    'pause',
    'printing',
    'cover_opened',
    'temperature'
];

const STATUS_QUERY = new Uint8Array([0x1b, 0x21, 0x3f]);

/**
 * Redondea el ancho al múltiplo de 8 que exige el BITMAP.
 *
 * @param {number} dots
 * @returns {number}
 */
function alignDots(dots) {
    const safe = Math.max(BITS_PER_BYTE, dots);
    return Math.ceil(safe / BITS_PER_BYTE) * BITS_PER_BYTE;
}

/**
 * @param {number} dots
 * @returns {string}
 */
function dotsToMm(dots) {
    const mm = Math.round((dots / DOTS_PER_MM) * 1000) / 1000;
    return String(mm);
}

/**
 * @param {string} text
 * @returns {Uint8Array}
 */
function asciiBytes(text) {
    const bytes = new Uint8Array(text.length);
    for (let index = 0; index < text.length; index += 1) {
        bytes[index] = text.charCodeAt(index) & 0xff;
    }
    return bytes;
}

/**
 * @param {Uint8Array[]} parts
 * @returns {Uint8Array}
 */
function concatBytes(parts) {
    let length = 0;
    for (let index = 0; index < parts.length; index += 1) {
        length += parts[index].length;
    }
    const out = new Uint8Array(length);
    let offset = 0;
    for (let index = 0; index < parts.length; index += 1) {
        out.set(parts[index], offset);
        offset += parts[index].length;
    }
    return out;
}

/**
 * 0 bit = negro. El blanco deja el bit a 1.
 *
 * @param {Uint8ClampedArray|Uint8Array} pixels RGBA
 * @param {number} width
 * @param {number} height
 * @returns {Uint8Array}
 */
function packImage(pixels, width, height) {
    const widthBytes = width / BITS_PER_BYTE;
    const packed = new Uint8Array(widthBytes * height);
    let out = 0;

    for (let y = 0; y < height; y += 1) {
        for (let xByte = 0; xByte < widthBytes; xByte += 1) {
            let value = 0;
            for (let bit = 0; bit < BITS_PER_BYTE; bit += 1) {
                const x = (xByte * BITS_PER_BYTE) + bit;
                const offset = ((y * width) + x) * 4;
                const luminance = (0.2126 * pixels[offset]) + (0.7152 * pixels[offset + 1]) + (0.0722 * pixels[offset + 2]);
                if (luminance >= 128) {
                    value |= 1 << (7 - bit);
                }
            }
            packed[out] = value;
            out += 1;
        }
    }
    return packed;
}

/**
 * @param {{ widthDots: number, heightDots: number, bitmap: Uint8Array, copies: number }} label
 * @returns {Uint8Array}
 */
function buildBitmapFrame(label) {
    const widthDots = alignDots(label.widthDots);
    const heightDots = label.heightDots;
    const widthBytes = widthDots / BITS_PER_BYTE;
    const copies = label.copies > 0 ? label.copies : 1;
    const header = [
        'SIZE ' + dotsToMm(widthDots) + ' mm,' + dotsToMm(heightDots) + ' mm',
        'GAP 3 mm,0 mm',
        'DIRECTION 0,0',
        'SET RESPONSE ON',
        'REFERENCE 0,0',
        'DENSITY 4',
        'SET TEAR 1',
        'CLS',
        'BITMAP 0,0,' + widthBytes + ',' + heightDots + ',0,'
    ].join('\r\n');
    const footer = 'PRINT 1,' + copies + '\r\nSOUND 2,100\r\n';
    return concatBytes([asciiBytes(header), label.bitmap, asciiBytes(footer)]);
}

/**
 * @param {Uint8Array} frame
 * @param {number} chunkSize
 * @returns {Uint8Array[]}
 */
function chunkBytes(frame, chunkSize) {
    const size = chunkSize > 0 ? chunkSize : frame.length;
    const chunks = [];
    for (let start = 0; start < frame.length; start += size) {
        chunks.push(frame.slice(start, start + size));
    }
    return chunks;
}

/**
 * @param {Uint8Array} bytes
 * @returns {number}
 */
function parsePrintedCount(bytes) {
    if (!bytes || bytes.length < 4) {
        return NaN;
    }
    let digits = '';
    for (let index = 3; index < bytes.length - 1; index += 1) {
        digits += String.fromCharCode(bytes[index]);
    }
    const count = parseInt(digits, 10);
    return Number.isFinite(count) ? count : NaN;
}

/**
 * @param {number} value
 * @returns {{ available: boolean, reason: string|null }}
 */
function decodeStatusByte(value) {
    const byte = value & 0xff;
    for (let bit = 0; bit < STATUS_BITS.length; bit += 1) {
        if ((byte >> bit) & 1) {
            return { available: false, reason: STATUS_BITS[bit] };
        }
    }
    return { available: true, reason: null };
}

export {
    DOTS_PER_MM,
    STATUS_BITS,
    STATUS_QUERY,
    alignDots,
    asciiBytes,
    buildBitmapFrame,
    chunkBytes,
    decodeStatusByte,
    packImage,
    parsePrintedCount
};
